import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ReactQuill, { Quill } from 'react-quill';
import { Link2, X } from 'lucide-react';
import Tooltip from '../Tooltip/Tooltip'; // Adjust path if necessary based on your structure
import { User } from '../../../interfaces/User';
import Ticket from '../../../interfaces/ticket/Ticket';
import { useAvatarCache } from '../../../contexts/AvatarContext';

import 'react-quill/dist/quill.snow.css';
import 'quill-mention/dist/quill.mention.css';
import './RichTextEditor.css';

// Normalize both ESM and CJS shapes:
import MentionModule from 'quill-mention';
const Mention = (MentionModule as any).Mention ?? MentionModule;
const MentionBlot = (MentionModule as any).MentionBlot ?? (MentionModule as any).Blot ?? undefined;

// Register safely
Quill.register({ 'modules/mention': Mention });
if (MentionBlot) {
    Quill.register({ 'blots/mention': MentionBlot });
}

type ToolbarVariant = 'full' | 'simple';

interface RichTextEditorProps {
    value: string;
    onChange: (value: string) => void;
    users?: User[];
    placeholder?: string;
    readOnly?: boolean;
    /** Retain the editor draft while its containing panel is hidden. */
    active?: boolean;
    roundedAvatars?: boolean;
    toolbarVariant?: ToolbarVariant;
    searchTickets?: (searchTerm: string) => Promise<Ticket[]>;
}

interface LinkEditorState {
    range: { index: number; length: number };
    selectedText: string;
    displayText: string;
    url: string;
}

interface LinkPanelPosition {
    left: number;
    top: number;
    width: number;
    maxHeight: number;
    placement: 'top' | 'bottom';
}

const escapeHtml = (value: string): string => value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

// --- Custom Toolbar Component ---
const CustomQuillToolbar = ({ id, variant, isLinkEditorOpen, toolbarRef, active }: { active: boolean, id: string, variant: 'full' | 'simple', isLinkEditorOpen: boolean, toolbarRef: React.RefObject<HTMLDivElement> }) => (
    <div ref={toolbarRef} id={id} className="relative z-10 !rounded-t-xl !border-0 !border-b !border-gray-100 !bg-gray-50/50 px-3 py-2 flex flex-wrap gap-y-2 dark:!border-slate-700 dark:!bg-slate-800">
        
        {/* Text Formatting Group */}
        <div className="mr-3 flex items-center gap-1 border-r border-gray-200 pr-3 dark:border-slate-600">
            <Tooltip active={active} content="Bold" position="top"><button className="ql-bold" /></Tooltip>
            <Tooltip active={active} content="Italic" position="top"><button className="ql-italic" /></Tooltip>
            <Tooltip active={active} content="Underline" position="top"><button className="ql-underline" /></Tooltip>
            {variant === 'full' && <Tooltip active={active} content="Strikethrough" position="top"><button className="ql-strike" /></Tooltip>}
        </div>

        {/* Lists & Indent Group */}
        <div className="mr-3 flex items-center gap-1 border-r border-gray-200 pr-3 dark:border-slate-600">
            <Tooltip active={active} content="Ordered List" position="top"><button className="ql-list" value="ordered" /></Tooltip>
            <Tooltip active={active} content="Bullet List" position="top"><button className="ql-list" value="bullet" /></Tooltip>
            {variant === 'full' && (
                <>
                    <Tooltip active={active} content="Decrease Indent" position="top"><button className="ql-indent" value="-1" /></Tooltip>
                    <Tooltip active={active} content="Increase Indent" position="top"><button className="ql-indent" value="+1" /></Tooltip>
                </>
            )}
        </div>

        {/* Blocks & Headers Group */}
        {variant === 'full' && (
            <div className="mr-3 flex items-center gap-1 border-r border-gray-200 pr-3 dark:border-slate-600">
                <Tooltip active={active} content="Heading 1" position="top"><button className="ql-header" value="1" /></Tooltip>
                <Tooltip active={active} content="Heading 2" position="top"><button className="ql-header" value="2" /></Tooltip>
                <Tooltip active={active} content="Blockquote" position="top"><button className="ql-blockquote" /></Tooltip>
                <Tooltip active={active} content="Code Block" position="top"><button className="ql-code-block" /></Tooltip>
            </div>
        )}

        {/* Links & Utilities Group */}
        {variant === 'full' && (
            <div className="flex items-center gap-1">
                <Tooltip active={active} content={isLinkEditorOpen ? "Close link editor" : "Insert link"} position="top">
                    <button
                        className={`ql-link ${isLinkEditorOpen ? 'ql-active' : ''}`}
                        aria-label={isLinkEditorOpen ? 'Close link options' : 'Insert link'}
                        aria-pressed={isLinkEditorOpen}
                    />
                </Tooltip>
                <Tooltip active={active} content="Clear Formatting" position="top"><button className="ql-clean" /></Tooltip>
            </div>
        )}
    </div>
);

const LinkEditorPopover = ({ anchor, children }: { anchor: HTMLElement | null; children: React.ReactNode }) => {
    const panelRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState<LinkPanelPosition | null>(null);

    useLayoutEffect(() => {
        if (!anchor) return;

        const updatePosition = () => {
            const anchorRect = anchor.getBoundingClientRect();
            const panelHeight = panelRef.current?.getBoundingClientRect().height ?? 250;
            const gutter = 8;
            const viewportPadding = 12;
            const width = Math.min(anchorRect.width, window.innerWidth - viewportPadding * 2);
            const left = Math.min(
                Math.max(viewportPadding, anchorRect.left),
                Math.max(viewportPadding, window.innerWidth - width - viewportPadding),
            );
            const roomAbove = anchorRect.top - viewportPadding - gutter;
            const roomBelow = window.innerHeight - anchorRect.bottom - viewportPadding - gutter;
            const placement: LinkPanelPosition['placement'] = roomAbove >= Math.min(panelHeight, 180) || roomAbove >= roomBelow ? 'top' : 'bottom';
            const maxHeight = Math.max(120, placement === 'top' ? roomAbove : roomBelow);
            const top = placement === 'top'
                ? Math.max(viewportPadding, anchorRect.top - Math.min(panelHeight, maxHeight) - gutter)
                : anchorRect.bottom + gutter;

            setPosition({ left, top, width, maxHeight, placement });
        };

        updatePosition();
        const frame = window.requestAnimationFrame(updatePosition);
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            window.cancelAnimationFrame(frame);
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [anchor]);

    return createPortal(
        <div
            ref={panelRef}
            data-click-outside-ignore="true"
            className={`modern-link-popover modern-link-popover--${position?.placement ?? 'top'}`}
            style={position ? {
                left: position.left,
                top: position.top,
                width: position.width,
                maxHeight: position.maxHeight,
                visibility: 'visible',
            } : { visibility: 'hidden' }}
        >
            {children}
        </div>,
        document.body,
    );
};

const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, users = [], placeholder, readOnly, active = true, roundedAvatars = false, toolbarVariant = 'full', searchTickets }) => {
    const quillRef = useRef<ReactQuill | null>(null);
    const toolbarRef = useRef<HTMLDivElement>(null);
    const [linkEditor, setLinkEditor] = useState<LinkEditorState | null>(null);
    const linkEditorOpenRef = useRef(false);
    linkEditorOpenRef.current = Boolean(linkEditor);
    const usersRef = useRef(users);
    usersRef.current = users;
    const searchTicketsRef = useRef(searchTickets);
    searchTicketsRef.current = searchTickets;
    const { getAvatarUrl } = useAvatarCache();
    const visibility = useRef({ active, generation: 0 });
    if (visibility.current.active !== active) {
        visibility.current = { active, generation: visibility.current.generation + 1 };
    }
    const roundedAvatarsRef = useRef(roundedAvatars);
    roundedAvatarsRef.current = roundedAvatars;

    useLayoutEffect(() => {
        visibility.current.active = active;
        const editor = quillRef.current?.getEditor();
        if (!active) {
            setLinkEditor(null);
            editor?.getModule?.('mention')?.hideMentionList?.();
            editor?.blur?.();
        }
        return () => {
            visibility.current.active = false;
            visibility.current.generation++;
            editor?.getModule?.('mention')?.hideMentionList?.();
        };
    }, [active]);
    
    // Generate a unique ID so multiple editors on the same page don't share toolbars
    const toolbarId = useMemo(() => `toolbar-${Math.random().toString(36).substring(2, 9)}`, []);

    const closeLinkEditor = useCallback(() => {
        setLinkEditor(null);
        const generation = visibility.current.generation;
        window.setTimeout(() => {
            if (visibility.current.active && visibility.current.generation === generation) quillRef.current?.getEditor().focus();
        }, 0);
    }, []);

    const toggleLinkEditor = useCallback(() => {
        if (!visibility.current.active) return;
        if (linkEditorOpenRef.current) {
            closeLinkEditor();
            return;
        }

        const quill = quillRef.current?.getEditor();
        if (!quill) return;

        const range = quill.getSelection(true) ?? { index: quill.getLength() - 1, length: 0 };
        const selectedText = range.length > 0 ? quill.getText(range.index, range.length) : '';
        const currentUrl = range.length > 0 ? String(quill.getFormat(range).link ?? '') : '';

        setLinkEditor({
            range,
            selectedText,
            displayText: selectedText,
            url: currentUrl,
        });
    }, [closeLinkEditor]);

    const saveLink = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!linkEditor || !visibility.current.active) return;

        const quill = quillRef.current?.getEditor();
        const url = linkEditor.url.trim();
        const displayText = linkEditor.displayText.trim();
        if (!quill || !url || (!linkEditor.selectedText && !displayText)) return;

        if (linkEditor.range.length > 0) {
            quill.formatText(linkEditor.range.index, linkEditor.range.length, 'link', url, 'user');
            quill.setSelection(linkEditor.range.index + linkEditor.range.length, 0, 'silent');
        } else {
            quill.insertText(linkEditor.range.index, displayText, 'link', url, 'user');
            quill.setSelection(linkEditor.range.index + displayText.length, 0, 'silent');
        }

        setLinkEditor(null);
        const generation = visibility.current.generation;
        window.setTimeout(() => {
            if (visibility.current.active && visibility.current.generation === generation) quill.focus();
        }, 0);
    }, [linkEditor]);

    const modules = useMemo(() => ({
        toolbar: {
            container: `#${toolbarId}`,
            handlers: {
                link: toggleLinkEditor,
            },
        },
        mention: {
            allowedChars: (denotationChar: string) => denotationChar === '#'
                ? /^[A-Za-z0-9\s-]*$/
                : /^[A-Za-z\sÅÄÖåäö]*$/,
            mentionDenotationChars: searchTickets ? ["@", "#"] : ["@"],
            positioningStrategy: 'fixed',
            mentionContainerClass: 'ql-mention-list-container modern-mention-list',
            renderLoading: () => `
                <div class="mention-state" role="status">
                    <span class="mention-state__spinner" aria-hidden="true"></span>
                    <span>Searching...</span>
                </div>
            `,
            source: async (searchTerm: string, renderList: (values: any[], searchTerm: string) => void, denotationChar: string) => {
                if (!visibility.current.active) return;
                const generation = visibility.current.generation;
                const isCurrent = () => visibility.current.active && visibility.current.generation === generation;
                if (denotationChar === '#') {
                    try {
                        const tickets = await searchTicketsRef.current?.(searchTerm) ?? [];
                        if (!isCurrent()) return;
                        const results = tickets.map(ticket => ({
                            id: ticket.id,
                            value: ticket.publicId,
                            title: ticket.title,
                            status: ticket.status,
                            link: `/issue/${encodeURIComponent(ticket.publicId)}`,
                            target: '_self',
                            referenceType: 'ticket',
                        }));
                        renderList(results.length > 0 ? results : [{
                            id: 'ticket-empty-state',
                            value: searchTerm ? 'No matching tickets' : 'No tickets yet',
                            description: searchTerm ? 'Try a different ticket ID or title.' : 'New tickets will appear here when available.',
                            disabled: true,
                            referenceType: 'state',
                        }], searchTerm);
                    } catch (error) {
                        if (!isCurrent()) return;
                        console.warn('Unable to search ticket references.', error);
                        renderList([{
                            id: 'ticket-error-state',
                            value: 'Tickets could not be loaded',
                            description: 'Check your connection and try again.',
                            disabled: true,
                            referenceType: 'state',
                        }], searchTerm);
                    }
                    return;
                }

                const currentUsers = usersRef.current;
                let matches: User[];
                if (searchTerm.length === 0) {
                    matches = currentUsers;
                } else {
                    const lowerCaseSearchTerm = searchTerm.toLowerCase();
                    matches = currentUsers.filter(user =>
                        user.displayName.toLowerCase().includes(lowerCaseSearchTerm) ||
                        (user.email && user.email.toLowerCase().includes(lowerCaseSearchTerm))
                    );
                }

                const promises = matches.map(user =>
                    getAvatarUrl(user.id)
                        .catch(() => null)
                        .then(avatarSrc => ({
                            id: user.id,
                            value: user.displayName,
                            email: user.email,
                            avatar: avatarSrc
                        }))
                );

                Promise.all(promises).then(usersWithAvatars => {
                    if (!isCurrent()) return;
                    renderList(usersWithAvatars.length > 0 ? usersWithAvatars : [{
                        id: 'user-empty-state',
                        value: 'No matching people',
                        description: 'Try a different name or email address.',
                        disabled: true,
                        referenceType: 'state',
                    }], searchTerm);
                });
            },
            // Semantic classes keep body-mounted mention suggestions themed.
            renderItem: (item: any) => {
                if (item.referenceType === 'state') {
                    return `
                        <div class="mention-state mention-state--message">
                            <div class="mention-state__copy">
                                <div class="mention-state__title">${escapeHtml(item.value)}</div>
                                <div class="mention-state__description">${escapeHtml(item.description || '')}</div>
                            </div>
                        </div>
                    `;
                }

                if (item.referenceType === 'ticket') {
                    return `
                        <div class="ticket-reference-option flex w-full items-center gap-3 px-3 py-2.5 transition-colors">
                            <div class="ticket-reference-option__icon flex h-8 w-8 shrink-0 items-center justify-center rounded-lg font-mono text-sm font-bold">#</div>
                            <div class="flex min-w-0 flex-1 flex-col leading-tight">
                                <div class="ticket-reference-option__id truncate font-mono text-xs font-semibold">${escapeHtml(item.value)}</div>
                                <div class="ticket-reference-option__title mt-1 truncate text-sm">${escapeHtml(item.title || 'Untitled ticket')}</div>
                            </div>
                            <div class="ticket-reference-option__status shrink-0 text-[10px] font-semibold uppercase tracking-wide">${escapeHtml(item.status || '')}</div>
                        </div>
                    `;
                }

                const safeValue = escapeHtml(item.value || '');
                const safeEmail = escapeHtml(item.email || '');
                const initials = safeValue.split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase();
                const avatarShape = roundedAvatarsRef.current ? 'rounded-lg' : 'rounded-full';
                const avatarDisplay = item.avatar
                    ? `<img src="${escapeHtml(item.avatar)}" class="w-full h-full ${avatarShape} object-cover" alt="Avatar for ${safeValue}" />`
                    : `<div class="flex items-center justify-center w-full h-full bg-[#7E57C2] text-white font-bold text-[10px] ${avatarShape}">${initials}</div>`;

                return `
                    <div class="user-mention-option flex items-center px-3 py-2 transition-colors w-full">
                        <div class="user-mention-option__avatar w-8 h-8 mr-3 flex-shrink-0 ${avatarShape} flex items-center justify-center shadow-sm">
                            ${avatarDisplay}
                        </div>
                        <div class="flex flex-col overflow-hidden whitespace-nowrap leading-tight">
                            <div class="user-mention-option__name font-semibold text-sm truncate">${safeValue}</div>
                            <div class="user-mention-option__email text-xs truncate">${safeEmail}</div>
                        </div>
                    </div>
                `;
            },
            onSelect: (item: any, insertItem: (data: any) => void) => {
                if (visibility.current.active) insertItem(item);
            },
        },
    }), [getAvatarUrl, searchTickets, toggleLinkEditor, toolbarId]);

    const formats =
        toolbarVariant === 'simple'
            ? ['bold', 'italic', 'underline', 'list', 'bullet']
            : [
                'header',
                'bold', 'italic', 'underline', 'strike', 'blockquote',
                'list', 'bullet', 'indent',
                'link', 'code-block', 'mention',
            ];

    return (
        <div className="modern-quill-wrapper relative flex flex-col w-full">
            {!readOnly && (
                <div className="modern-link-controls relative z-30">
                    <CustomQuillToolbar active={active} id={toolbarId} variant={toolbarVariant} isLinkEditorOpen={Boolean(linkEditor)} toolbarRef={toolbarRef} />
                    {active && linkEditor && (
                        <LinkEditorPopover anchor={toolbarRef.current}>
                        <form className="modern-link-panel" onSubmit={saveLink} onKeyDown={event => {
                            if (event.key === 'Escape') {
                                event.preventDefault();
                                closeLinkEditor();
                            }
                        }}>
                            <div className="modern-link-panel__header">
                                <div className="modern-link-panel__heading">
                                    <span className="modern-link-panel__icon"><Link2 size={16} /></span>
                                    <div>
                                        <div className="modern-link-panel__title">Add link</div>
                                        <div className="modern-link-panel__subtitle">Link selected text or create new linked text.</div>
                                    </div>
                                </div>
                                <button type="button" className="modern-link-panel__close" onClick={closeLinkEditor} aria-label="Close link editor">
                                    <X size={16} />
                                </button>
                            </div>

                            <div className="modern-link-panel__fields">
                                {linkEditor.selectedText ? (
                                    <div className="modern-link-panel__selected-text">
                                        <span>Display text</span>
                                        <strong>{linkEditor.selectedText}</strong>
                                    </div>
                                ) : (
                                    <label className="modern-link-panel__field">
                                        <span>Display text</span>
                                        <input
                                            type="text"
                                            value={linkEditor.displayText}
                                            onChange={event => setLinkEditor(current => current ? { ...current, displayText: event.target.value } : current)}
                                            placeholder="Text people will see"
                                            autoFocus
                                        />
                                    </label>
                                )}
                                <label className="modern-link-panel__field">
                                    <span>Link URL</span>
                                    <input
                                        type="text"
                                        inputMode="url"
                                        value={linkEditor.url}
                                        onChange={event => setLinkEditor(current => current ? { ...current, url: event.target.value } : current)}
                                        placeholder="https://example.com"
                                        autoFocus={Boolean(linkEditor.selectedText)}
                                    />
                                </label>
                            </div>

                            <div className="modern-link-panel__actions">
                                <button type="button" className="modern-link-panel__cancel" onClick={closeLinkEditor}>Cancel</button>
                                <button
                                    type="submit"
                                    className="modern-link-panel__save"
                                    disabled={!linkEditor.url.trim() || (!linkEditor.selectedText && !linkEditor.displayText.trim())}
                                >
                                    Add link
                                </button>
                            </div>
                        </form>
                        </LinkEditorPopover>
                    )}
                </div>
            )}
            <ReactQuill
                ref={quillRef}
                theme="snow"
                value={value}
                onChange={(nextValue) => { if (visibility.current.active) onChange(nextValue); }}
                modules={modules}
                formats={formats}
                placeholder={placeholder || 'Describe the issue...'}
                readOnly={readOnly || !active}
                className="w-full"
            />
        </div>
    );
};

export default RichTextEditor;
